import { Service, SWRConfiguration, useGet, useList, useRead } from "@api/common";
import type {
  ListParams,
  ReadParams,
  Subscription,
  SubscriptionPlan,
  UpcomingInvoice,
} from "./types";

export class SubscriptionService extends Service<Subscription> {
  constructor() {
    super("/subscriptions");
  }

  async create(body: { org: string; token: string; paymentMethod: string }): Promise<Subscription> {
    return await this.post(this.basePath, body);
  }
  async updateConfiguration(id: string, body: { token: string }): Promise<Subscription> {
    return await this.put(`${this.basePath}/${id}/configuration`, body);
  }
  async payOutstandingBalance(id: string): Promise<Subscription> {
    return await this.post(`${this.basePath}/${id}/purchase-incremental`);
  }
}

export const subscriptionService = new SubscriptionService();

export function useSubscriptions(params?: ListParams<Subscription>, config?: SWRConfiguration) {
  return useList(subscriptionService, { sort: "-currentInvoiceDate", ...params }, config);
}

export function useSubscriptionPlans(params?: ListParams<Subscription>, config?: SWRConfiguration) {
  return useGet<SubscriptionPlan[]>(subscriptionService, "/subscriptions/plans", params, config);
}

export function useSubscription(id: string, params?: ReadParams, config?: SWRConfiguration) {
  return useRead(subscriptionService, id, params, config);
}
export function useSubscriptionUpcomingInvoice(
  id: string,
  params?: ReadParams & { cancel?: boolean; token?: string },
  config?: SWRConfiguration,
) {
  return useGet<UpcomingInvoice>(
    subscriptionService,
    id ? `${subscriptionService.basePath}/${id}/upcoming-invoice` : null,
    params,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      ...config,
    },
  );
}
export function useSubscriptionCurrentUsage(
  id: string,
  params?: ReadParams,
  config?: SWRConfiguration,
) {
  return useGet<{ date: string; [key: string]: any }[]>(
    subscriptionService,
    `${subscriptionService.basePath}/${id}/current-usage`,
    params,
    config,
  );
}
